import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { useTaxonomy } from '../../hooks/useTaxonomy';
import { API_BASE } from '../../config/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text (Single Line)' },
  { value: 'textarea', label: 'Text Area (Multi Line)' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown (Select)' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkboxes' },
  { value: 'file', label: 'File Upload' }
];

const TAXONOMY_TYPES = [
  { key: 'level_of_study', label: 'Level of Study' },
  { key: 'field_of_study', label: 'Field of Study' },
  { key: 'subject', label: 'Subject' },
  { key: 'mode_of_study', label: 'Mode of Study' },
  { key: 'intake', label: 'Intake' },
  { key: 'institution_type', label: 'Institution Type' },
  { key: 'scholarship_type', label: 'Scholarship Type' },
  { key: 'article_category', label: 'Article Category' }
];

export const FieldRegistry = () => {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    key: '',
    label: '',
    field_type: 'text',
    description: '',
    placeholder: '',
    required: false,
    taxonomy_key: '',
    options: [],
    upload_constraints: {
      allowed_types: ['pdf', 'jpg', 'jpeg', 'png'],
      max_size_mb: 5.0,
      max_count: 1
    },
    validation_regex: '',
    validation_message: ''
  });

  const [optionInput, setOptionInput] = useState({ value: '', label: '' });

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/form-fields`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch fields');

      const data = await response.json();
      setFields(data.fields || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingField(null);
    setFormData({
      key: '',
      label: '',
      field_type: 'text',
      description: '',
      placeholder: '',
      required: false,
      taxonomy_key: '',
      options: [],
      upload_constraints: {
        allowed_types: ['pdf', 'jpg', 'jpeg', 'png'],
        max_size_mb: 5.0,
        max_count: 1
      },
      validation_regex: '',
      validation_message: ''
    });
    setShowModal(true);
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      key: field.key || '',
      label: field.label || '',
      field_type: field.field_type || 'text',
      description: field.description || '',
      placeholder: field.placeholder || '',
      required: field.required || false,
      taxonomy_key: field.taxonomy_key || '',
      options: field.options || [],
      upload_constraints: field.upload_constraints || {
        allowed_types: ['pdf', 'jpg', 'jpeg', 'png'],
        max_size_mb: 5.0,
        max_count: 1
      },
      validation_regex: field.validation_regex || '',
      validation_message: field.validation_message || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    // Validation
    if (!formData.key.trim() || !formData.label.trim()) {
      alert('Key and Label are required');
      return;
    }

    // For option fields, validate taxonomy_key or options
    if (['select', 'radio', 'checkbox'].includes(formData.field_type)) {
      if (!formData.taxonomy_key && (!formData.options || formData.options.length === 0)) {
        alert('Option fields must have either a taxonomy key or fixed options');
        return;
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = editingField
        ? `${backendUrl}/api/admin/form-fields/${editingField.id}`
        : `${backendUrl}/api/admin/form-fields`;

      const method = editingField ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save field');
      }

      alert(editingField ? 'Field updated successfully' : 'Field created successfully');
      setShowModal(false);
      fetchFields();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (field) => {
    if (!window.confirm(`Are you sure you want to delete field "${field.label}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/form-fields/${field.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to delete field');
      }

      alert('Field deleted successfully');
      fetchFields();
    } catch (err) {
      alert(err.message);
    }
  };

  const addOption = () => {
    if (!optionInput.value.trim() || !optionInput.label.trim()) {
      alert('Both value and label are required');
      return;
    }

    setFormData({
      ...formData,
      options: [...formData.options, { ...optionInput }]
    });
    setOptionInput({ value: '', label: '' });
  };

  const removeOption = (index) => {
    setFormData({
      ...formData,
      options: formData.options.filter((_, i) => i !== index)
    });
  };

  const toggleFileType = (type) => {
    const current = formData.upload_constraints.allowed_types || [];
    const updated = current.includes(type)
      ? current.filter(t => t !== type)
      : [...current, type];
    
    setFormData({
      ...formData,
      upload_constraints: {
        ...formData.upload_constraints,
        allowed_types: updated
      }
    });
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">Field Registry</h1>
            <p className="text-sm text-[#6B7280] mt-1">Manage reusable form fields</p>
          </div>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors"
          >
            + Create Field
          </button>
        </div>

        {/* Fields Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-3 border-[#1E40AF] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-2 text-sm text-[#6B7280]">Loading fields...</p>
          </div>
        ) : fields.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-[#E5E7EB]">
            <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-[#111827] mb-2">No Fields Yet</h3>
            <p className="text-sm text-[#6B7280] mb-4">Create your first reusable field to get started</p>
            <button
              onClick={handleCreate}
              className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors"
            >
              + Create Field
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg border-2 border-[#E5E7EB] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Label</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Options Source</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Required</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {fields.map(field => (
                  <tr key={field.id} className="hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-6 py-4 text-sm font-mono text-[#374151]">{field.key}</td>
                    <td className="px-6 py-4 text-sm text-[#111827] font-medium">{field.label}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs font-medium bg-[#EEF2FF] text-[#4F46E5] rounded">
                        {field.field_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-[#6B7280]">
                      {field.taxonomy_key ? (
                        <span className="px-2 py-1 text-xs font-medium bg-[#DBEAFE] text-[#1E40AF] rounded">
                          Taxonomy: {field.taxonomy_key}
                        </span>
                      ) : field.options && field.options.length > 0 ? (
                        <span className="px-2 py-1 text-xs font-medium bg-[#FEF3C7] text-[#92400E] rounded">
                          Fixed ({field.options.length} options)
                        </span>
                      ) : field.field_type === 'file' ? (
                        <span className="px-2 py-1 text-xs font-medium bg-[#FEE2E2] text-[#991B1B] rounded">
                          Upload
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {field.required ? (
                        <span className="text-xs font-semibold text-[#DC2626]">Required</span>
                      ) : (
                        <span className="text-xs text-[#9CA3AF]">Optional</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleEdit(field)}
                        className="text-sm text-[#1E40AF] hover:text-[#1e3a8a] font-medium mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(field)}
                        className="text-sm text-[#DC2626] hover:text-[#b91c1c] font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-[#111827]">
                  {editingField ? 'Edit Field' : 'Create New Field'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F3F4F6] transition-colors"
                >
                  <svg className="w-5 h-5 text-[#6B7280]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">
                      Field Key * <span className="text-xs font-normal text-[#6B7280]">(Unique, lowercase, underscore)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.key}
                      onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                      placeholder="e.g., full_name, email, resume"
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF] font-mono text-sm"
                      disabled={editingField !== null}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Display Label *</label>
                    <input
                      type="text"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder="e.g., Full Name, Email Address"
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Field Type *</label>
                    <select
                      value={formData.field_type}
                      onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                    >
                      {FIELD_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.required}
                        onChange={(e) => setFormData({ ...formData, required: e.target.checked })}
                        className="w-4 h-4 text-[#1E40AF] border-2 border-[#E5E7EB] rounded focus:ring-2 focus:ring-[#1E40AF]"
                      />
                      <span className="text-sm font-semibold text-[#111827]">Required Field</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Description (Help Text)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional help text shown below the field"
                    rows={2}
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#111827] mb-2">Placeholder</label>
                  <input
                    type="text"
                    value={formData.placeholder}
                    onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
                    placeholder="Placeholder text inside the input"
                    className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  />
                </div>

                {/* Option Fields Configuration */}
                {['select', 'radio', 'checkbox'].includes(formData.field_type) && (
                  <div className="border-2 border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                    <h3 className="text-sm font-bold text-[#111827] mb-3">Options Configuration</h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-[#111827] mb-2">
                        Link to Taxonomy (Recommended)
                      </label>
                      <select
                        value={formData.taxonomy_key}
                        onChange={(e) => setFormData({ ...formData, taxonomy_key: e.target.value, options: [] })}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                      >
                        <option value="">-- Select Taxonomy (or define fixed options below) --</option>
                        {TAXONOMY_TYPES.map(tax => (
                          <option key={tax.key} value={tax.key}>{tax.label}</option>
                        ))}
                      </select>
                    </div>

                    {!formData.taxonomy_key && (
                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">
                          OR Define Fixed Options
                        </label>
                        
                        <div className="flex gap-2 mb-3">
                          <input
                            type="text"
                            value={optionInput.value}
                            onChange={(e) => setOptionInput({ ...optionInput, value: e.target.value })}
                            placeholder="Value (e.g., bachelor)"
                            className="flex-1 px-3 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF] text-sm"
                          />
                          <input
                            type="text"
                            value={optionInput.label}
                            onChange={(e) => setOptionInput({ ...optionInput, label: e.target.value })}
                            placeholder="Label (e.g., Bachelor's Degree)"
                            className="flex-1 px-3 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF] text-sm"
                          />
                          <button
                            onClick={addOption}
                            className="px-4 py-2 bg-[#1E40AF] text-white rounded-lg hover:bg-[#1e3a8a] transition-colors text-sm font-medium"
                          >
                            Add
                          </button>
                        </div>

                        {formData.options.length > 0 && (
                          <div className="space-y-2">
                            {formData.options.map((opt, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-white px-3 py-2 rounded border border-[#E5E7EB]">
                                <div className="text-sm">
                                  <span className="font-mono text-[#6B7280]">{opt.value}</span>
                                  <span className="mx-2 text-[#9CA3AF]">→</span>
                                  <span className="text-[#111827]">{opt.label}</span>
                                </div>
                                <button
                                  onClick={() => removeOption(idx)}
                                  className="text-[#DC2626] hover:text-[#b91c1c] text-xs font-medium"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* File Upload Configuration */}
                {formData.field_type === 'file' && (
                  <div className="border-2 border-[#E5E7EB] rounded-lg p-4 bg-[#F9FAFB]">
                    <h3 className="text-sm font-bold text-[#111827] mb-3">File Upload Constraints</h3>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-[#111827] mb-2">Allowed File Types</label>
                      <div className="flex flex-wrap gap-2">
                        {['pdf', 'jpg', 'jpeg', 'png'].map(type => (
                          <label key={type} className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-[#E5E7EB] rounded-lg cursor-pointer hover:border-[#1E40AF] transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.upload_constraints.allowed_types.includes(type)}
                              onChange={() => toggleFileType(type)}
                              className="w-4 h-4 text-[#1E40AF] rounded"
                            />
                            <span className="text-sm font-medium text-[#111827] uppercase">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Max File Size (MB)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0.1"
                          max="10"
                          value={formData.upload_constraints.max_size_mb}
                          onChange={(e) => setFormData({
                            ...formData,
                            upload_constraints: {
                              ...formData.upload_constraints,
                              max_size_mb: parseFloat(e.target.value)
                            }
                          })}
                          className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Max File Count</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={formData.upload_constraints.max_count}
                          onChange={(e) => setFormData({
                            ...formData,
                            upload_constraints: {
                              ...formData.upload_constraints,
                              max_count: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg bg-white focus:outline-none focus:border-[#1E40AF]"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white border-t border-[#E5E7EB] px-6 py-4 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-white border-2 border-[#E5E7EB] text-[#6B7280] font-semibold rounded-lg hover:bg-[#F3F4F6] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-6 py-2 bg-[#1E40AF] hover:bg-[#1e3a8a] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : (editingField ? 'Update Field' : 'Create Field')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FieldRegistry;
